import {
  customElement,
  type TemplateResult,
  property,
  html,
  css,
  cssManager,
} from '@design.estate/dees-element';
import { DeesInputBase } from '../dees-input-base/dees-input-base.js';
import { demoFunc } from './dees-input-checkbox.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-input-checkbox': DeesInputCheckbox;
  }
}

@customElement('dees-input-checkbox')
export class DeesInputCheckbox extends DeesInputBase<DeesInputCheckbox> {
  public static inputLayout = 'choice' as const;
  // STATIC
  public static demo = demoFunc;
  public static demoGroups = ['Input'];

  // INSTANCE

  @property({
    type: Boolean,
  })
  accessor value: boolean = false;

  @property({ type: Boolean })
  accessor indeterminate: boolean = false;

  /** Accessible name for a checkbox whose visible label is omitted. */
  @property({ type: String, attribute: 'aria-label' })
  override accessor ariaLabel: string | null = null;

  /** Tab order for the inner control, including programmatic-only controls. */
  @property({ type: Number, attribute: 'control-tabindex' })
  accessor controlTabIndex = 0;

  constructor() {
    super();
    this.labelPosition = 'right'; // Checkboxes default to label on the right
  }

  public static styles = [
    themeDefaultStyles,
    ...DeesInputBase.baseStyles,
    cssManager.defaultStyles,
    css`
        /* TODO: Migrate hardcoded values to --dees-* CSS variables */
        * {
          box-sizing: border-box;
        }

        :host {
          position: relative;
          cursor: default;
          font-family: var(--dees-font-family);
        }

        .maincontainer {
          display: inline-flex;
          align-items: flex-start;
          gap: var(--dees-spacing-sm);
          cursor: pointer;
          user-select: none;
          transition: all var(--dees-transition-fast) var(--dees-ease-standard);
        }

        .checkbox {
          position: relative;
          height: 18px;
          width: 18px;
          flex-shrink: 0;
          border-radius: var(--dees-radius-sm);
          border: 1px solid var(--dees-color-border-default);
          background: var(--dees-color-bg-primary);
          transition: all var(--dees-transition-fast) var(--dees-ease-standard);
          margin-top: 1px;
        }

        .maincontainer:hover .checkbox {
          border-color: var(--dees-color-border-strong);
        }

        .checkbox.selected {
          background: var(--dees-color-accent-primary);
          border-color: var(--dees-color-accent-primary);
        }

        .checkbox:focus-visible {
          outline: 2px solid var(--dees-color-focus-ring);
          outline-offset: 2px;
        }

        /* Checkmark using Lucide icon style */
        .checkbox .checkmark {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          opacity: 0;
          transition: opacity var(--dees-transition-fast) var(--dees-ease-standard);
        }

        .checkbox.selected .checkmark {
          opacity: 1;
        }

        .checkbox .checkmark svg {
          width: 12px;
          height: 12px;
          stroke-width: 3;
        }

        /* Path carries stroke="currentColor"; target it directly so the
           checkmark is reliably white on the accent fill in both themes. */
        .checkbox .checkmark svg path {
          stroke: var(--dees-color-on-accent);
        }

        /* Disabled state */
        .maincontainer.disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .checkbox.disabled {
          background: var(--dees-color-badge-default-bg);
          border-color: var(--dees-color-border-default);
        }

        /* Label */
        .label-container {
          display: flex;
          flex-direction: column;
          gap: 2px;
          flex: 1;
        }

        .checkbox-label {
          font-size: var(--dees-font-control-size);
          font-weight: var(--dees-font-control-weight);
          line-height: var(--dees-font-subhead-line);
          color: var(--dees-color-text-primary);
          transition: color var(--dees-transition-fast) var(--dees-ease-standard);
          letter-spacing: -0.01em;
        }

        .maincontainer:hover .checkbox-label {
          color: var(--dees-color-text-primary);
        }

        .maincontainer.disabled:hover .checkbox-label {
          color: var(--dees-color-text-primary);
        }

        /* Label-left row layout (grouped forms / settings rows): the whole
           row spans full width and the checkbox sits at the trailing edge.
           Override the base grid, which would otherwise pin the control inside
           the narrow label column. */
        :host([label-position="left"]) .input-wrapper {
          display: block;
        }
        :host([label-position="left"]) .maincontainer {
          display: flex;
          width: 100%;
          max-width: none;
          align-items: center;
        }
        :host([label-position="left"]) .label-container {
          order: -1;
          flex: 1;
        }
        @media (pointer: coarse) { .maincontainer { min-height: 44px; align-items: center; } }

    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="input-wrapper">
        <div part="control" class="maincontainer ${this.disabled ? 'disabled' : ''}" @click="${this.toggleSelected}">
          <div 
            class="checkbox ${this.value ? 'selected' : ''} ${this.disabled ? 'disabled' : ''}" 
            role="checkbox" aria-checked=${this.indeterminate ? 'mixed' : String(this.value)}
            aria-disabled=${String(this.disabled)} aria-label=${this.ariaLabel || this.label || 'Checkbox'}
            tabindex=${this.disabled ? -1 : this.controlTabIndex}
            @keydown="${this.handleKeydown}"
          >
            ${this.value
              ? html`
                  <span class="checkmark">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                `
              : this.indeterminate
                ? html`
                    <span class="checkmark">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 12H19" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </span>
                  `
                : html``}
          </div>
          ${this.label || this.description ? html`<div class="label-container">
            ${this.label ? html`<div class="checkbox-label">${this.label}</div>` : ''}
            ${this.renderDescription()}
          </div>` : ''}
        </div>
      </div>
    `;
  }

  public async toggleSelected() {
    if (this.disabled) {
      return;
    }
    this.value = !this.value;
    this.dispatchEvent(
      new CustomEvent('newValue', {
        detail: this.value,
        bubbles: true,
      })
    );
    this.changeSubject.next(this);
  }

  public getValue(): boolean {
    return this.value;
  }

  public setValue(value: boolean): void {
    this.value = value;
  }

  public focus(): void {
    const checkboxDiv = this.shadowRoot!.querySelector('.checkbox');
    if (checkboxDiv) {
      (checkboxDiv as any).focus();
    }
  }

  private handleKeydown(event: KeyboardEvent) {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.toggleSelected();
    }
  }
}
